import { SleepIcon, ElementListIcon, BullsEyeIcon, CalendarIcon } from '../common/icons';
import urls from './urls';

export const appSettings = {
    keepPublicTokenForSession: false,
};

export const headerItems = {
    title: 'title',
    pageTitle: 'pageTitle',
    flex: 'flex',
    appItems: 'appItems',
    pages: 'pages',
    chat: 'chat',
    favourites: 'favourites',
    profile: 'profile',
    logout: 'logout',
    menu: 'menu',
    org: 'org',
    login: 'login',
    customActions: 'customActions',
};

export const dashboardItems = {
    welcome: 'welcome',
    programDescription: 'programDescription',
    questionnaires: 'questionnaires',
    intro: 'intro',
    modules: 'modules',
};

export const dashboardItemsCustom = {
    unfinished: 'unfinished',
    progress: 'progress',
    calendar: 'calendar',
    completedIntro: 'completedIntro',
    waitingForScheduled: 'waitingForScheduled',
    exit: 'exit',
    info: 'info',
};

const appPages = [
    {
        key: 'activityDiary', url: urls.inside.pages.activityDiary, nameKey: 'activityDiary.header', Icon: CalendarIcon,
    },
    {
        key: 'sleepDiary', url: urls.inside.pages.sleepDiary, nameKey: 'sleepDiary.header', Icon: SleepIcon,
    },
    {
        key: 'elementsPage', url: urls.inside.pages.elements, nameKey: 'elementsPage.heading', Icon: ElementListIcon, hideIfNoProgram: true,
    },
    {
        key: 'elementsPage-bullseye', url: `${urls.inside.pages.elements}/BullsEye`, nameKey: 'elementsPage.heading', Icon: BullsEyeIcon, hideIfNoProgram: true,
    },
];

const defaultSetup = {
    header: [headerItems.title, headerItems.flex, headerItems.pages, headerItems.appItems, headerItems.chat, headerItems.favourites, headerItems.menu],
    dashboard: [dashboardItems.welcome, dashboardItems.programDescription, dashboardItems.questionnaires, dashboardItems.intro, dashboardItems.modules],
    pages: [],
    visitTaskDelay: 0.2, // delay before a task is visited (in seconds)
    features: {
        profile: false,
        favourites: false,
        languages: false,
        menu: true,
        breadcrumb: true,
        progress: false,
        logout: false,
        org: false,

        // features that is mapped from OrgConfig
        notifications: false,
        activityDiary: false,
        chat: false,
        heightWeight: false,
        selfDelete: false,
        sleepDiary: false,
        videoChat: false,
        feedback: false,

        // Som features that are default off
        moduleProgress: false,
        sensors: false,
        videoPage: false,
        calendar: false,
        printsPage: false,
        showPrintPageButton: true,
        autoplayVideos: false,

        // Special features in layout/design
        useFlatModuleBoxes: false,
        useSecondaryBackroundInSideMenu: false,
    },
    patient: {
        pollState: false,
        mailAndPhone: true,
        sexAndBirthDate: true,
        profilePicture: true,

        // Som features that are default off
        checkMissingData: false,
        useUserNameAndPw: false,
    },
};

// Starts with  empty values to avoid flash of hidden content
const startupConfig = { appSetup: { header: [], dashboard: [] } };

const parseOrgConfigFeatures = (clientConfig) => {
    const values = [];
    if (clientConfig.features) {
        Object.entries(clientConfig.features).forEach(([key, value]) => {
            values.push({ key: `features.${key}`, value });
        });
    }
    if (clientConfig.appSetup) {
        Object.entries(clientConfig.appSetup).forEach(([key, value]) => {
            values.push({ key, value });
        });
    }

    return values;
};

const appSetup = (clientConfig = startupConfig) => {
    const overrideKeys = parseOrgConfigFeatures(clientConfig);
    const pagesFromFeatures = {
        sleepDiary: clientConfig.features?.sleepDiary,
        activityDiary: clientConfig.features?.activityDiary,
    };
    const pagesToShow = clientConfig.customs?.appSetupPages?.split(',').reduce((prev, curr) => ({ ...prev, [curr.trim()]: true }), pagesFromFeatures) || pagesFromFeatures;

    const appSetupCustoms = {
        header: clientConfig.customs?.appSetupHeader?.split(',').map(d => d.trim()) || defaultSetup.header,
        dashboard: clientConfig.customs?.appSetupDashboard?.split(',').map(d => d.trim()) || defaultSetup.dashboard,
        pages: appPages.filter(d => pagesToShow[d.key]),
    };

    const appSetupWithOverrides = overrideKeys.reduce((m, o) => {
        const keys = o.key.split('.');
        let cur = m;
        keys.forEach((key, i) => {
            if (i < keys.length - 1) {
                cur[key] = cur[key] || {};
                cur = cur[key];
            } else {
                cur[key] = o.value;
            }
        });
        return m;
    }, { ...defaultSetup, ...appSetupCustoms });

    // Set patient.pollState either by direct override or if chat is enabled
    appSetupWithOverrides.patient.pollState = appSetupWithOverrides.patient.pollState || appSetupWithOverrides.features.chat;

    // override default config from Helse Bergen
    appSetupWithOverrides.features.videoChat = false;

    return {
        ...clientConfig,
        features: undefined, // is moved into appSetup..
        appName: clientConfig.appName,
        customs: clientConfig.customs || {},
        appSetup: appSetupWithOverrides,
    };
};

export default appSetup;
